import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Plus, Search, Code, Trash2, Edit3, Activity, Terminal, ChevronRight, ChevronLeft, RefreshCw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';

const ActionScriptList = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const isRTL = i18n.language === 'ar' || i18n.language === 'ur';

    // States
    const [scripts, setScripts] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [skip, setSkip] = useState(0);
    const limit = 10;

    const fetchScripts = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(`/api/admin/workflow/scripts?skip=${skip}&limit=${limit}&search=${searchTerm}`);
            setScripts(res.data.data);
            setTotal(res.data.total);
        } catch (err) { 
            toast.error(t('scripts.toasts.fetch_fail')); 
        } finally {
            setLoading(false);
        }
    }, [skip, searchTerm, t]);

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            fetchScripts();
        }, 500);
        return () => clearTimeout(delayDebounceFn);
    }, [fetchScripts]);

    return (
        <div className={`min-h-screen bg-[#0f172a] text-slate-200 p-8 ${isRTL ? 'font-cairo' : 'font-sans'}`}>
            <div className="max-w-6xl mx-auto">
                {/* Header */}
                <div className={`flex justify-between items-center mb-10 ${isRTL ? 'flex-row-reverse text-right' : 'text-left'}`}>
                    <div>
                        <h1 className={`text-3xl font-black text-white flex items-center gap-3 tracking-tighter ${isRTL ? 'flex-row-reverse' : ''}`}>
                            <Terminal className="text-orange-500" size={32} /> {t('scripts.title')}
                        </h1>
                        <p className="text-slate-400 text-xs mt-1 uppercase tracking-widest font-bold">{t('scripts.subtitle')}</p>
                    </div>
                    <button 
                        onClick={() => navigate('/admin/workflow/studio/new')}
                        className="bg-orange-600 hover:bg-orange-500 text-white px-6 py-3 rounded-md font-black flex items-center gap-2 transition-all shadow-lg hover:scale-105 uppercase text-xs"
                    >
                        <Plus size={18} /> {t('scripts.new_btn')}
                    </button>
                </div>

                {/* Table & Search Container */}
                <div className="bg-[#1e293b] border border-slate-700 rounded-xl overflow-hidden shadow-2xl">
                    <div className={`p-4 border-b border-slate-700 flex items-center gap-4 bg-[#1e293b]/50 ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <Search size={18} className="text-slate-500" />
                        <input 
                            placeholder={t('scripts.search_placeholder')} 
                            className={`bg-transparent border-none outline-none text-sm w-full font-semibold ${isRTL ? 'text-right' : 'text-left'}`}
                            value={searchTerm}
                            onChange={(e) => { setSearchTerm(e.target.value); setSkip(0); }}
                        />
                        {loading && <RefreshCw size={16} className="animate-spin text-orange-500" />}
                    </div>

                    <div className="overflow-x-auto">
                        <table className={`w-full ${isRTL ? 'text-right' : 'text-left'}`}>
                            <thead className="bg-[#0f172a]/50 text-[10px] font-black uppercase tracking-widest text-slate-500">
                                <tr className={isRTL ? 'flex-row-reverse' : ''}>
                                    <th className="p-4">{t('scripts.table.name')}</th>
                                    <th className="p-4">{t('scripts.table.object')}</th>
                                    <th className="p-4">{t('scripts.table.created')}</th>
                                    <th className="p-4">{t('workflow.table.status')}</th>
                                    <th className="p-4 text-center">{t('workflow.action')}</th>
                                </tr>
                            </thead>
                            <tbody className={`divide-y divide-slate-700 ${loading ? 'opacity-50' : ''}`}>
                                {scripts.length > 0 ? scripts.map(script => (
                                    <tr key={script.id} className="hover:bg-slate-800/50 transition-colors group">
                                        <td className="p-4">
                                            <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                                                <div className="p-2 bg-orange-500/10 rounded-lg text-orange-500"><Code size={18}/></div>
                                                <span className="font-bold text-white uppercase">{script.script_name}</span>
                                            </div>
                                        </td>
                                        <td className="p-4 text-xs font-black text-blue-400 uppercase tracking-tighter">{script.object_name}</td>
                                        <td className="p-4 text-xs text-slate-500">{new Date(script.created_at).toLocaleDateString()}</td>
                                        <td className="p-4">
                                            <span className="bg-green-500/10 text-green-500 text-[9px] px-2 py-1 rounded-full font-black uppercase">{t('common.active')}</span>
                                        </td>
                                        <td className="p-4">
                                            <div className={`flex justify-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                                                <button onClick={() => navigate(`/admin/workflow/studio/${script.id}`)} className="p-2 hover:bg-slate-700 rounded-md text-slate-400 hover:text-white transition-all shadow-sm"><Edit3 size={16}/></button>
                                                <button className="p-2 hover:bg-red-500/10 rounded-md text-slate-500 hover:text-red-500 transition-all shadow-sm"><Trash2 size={16}/></button>
                                            </div>
                                        </td>
                                    </tr>
                                )) : (
                                    <tr>
                                        <td colSpan="5" className="p-20 text-center text-slate-500 font-bold uppercase italic tracking-widest">{t('dashboard.no_data')}</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination Controls */}
                    <div className={`p-4 bg-[#0f172a]/30 border-t border-slate-700 flex justify-between items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <div className="text-[10px] font-black text-slate-500 uppercase">
                            {t('dashboard.showing')} {skip + 1} - {Math.min(skip + limit, total)} {t('dashboard.of')} {total}
                        </div>
                        <div className={`flex gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                            <button 
                                disabled={skip === 0}
                                onClick={() => setSkip(prev => Math.max(0, prev - limit))}
                                className="p-2 bg-slate-800 rounded-md disabled:opacity-20 hover:bg-slate-700 transition-all border border-slate-600"
                            >
                                {isRTL ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
                            </button>
                            <button 
                                disabled={skip + limit >= total}
                                onClick={() => setSkip(prev => prev + limit)}
                                className="p-2 bg-slate-800 rounded-md disabled:opacity-20 hover:bg-slate-700 transition-all border border-slate-600"
                            >
                                {isRTL ? <ChevronLeft size={18} /> : <ChevronRight size={18} />}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ActionScriptList;